import { useCallback, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ToastContext, useFetch } from '@reveni-io/reveni-ui'

import { toStoreDateString } from '@/common/helpers/dates'
import {
  loadStoredEditorValue,
  saveStoredEditorValue,
} from '@/configuration/pages/ConfigurationPage/OrderTrackingPageConfiguration/TrackingPageEditorModal/helpers'
import configurationServices from '@/configuration/services/services'

import { realOrderStorageKeyPrefix } from './constants'
import { getDisplayedRealOrder, getRealOrderPreview, isOrderNotFoundError } from './helpers'
import { transformResponseOrderTrackingPreviewOrder } from './transformers'

const useRealOrder = ({
  storeId,
  timeZone,
  orderTrackingPreview,
  layout,
  getBlockVisibility,
  criteriaOptions,
  isRealOrderTab,
}) => {
  const { t } = useTranslation()
  const { setToastInfo } = useContext(ToastContext)
  const [selectedRealOrderId, setSelectedRealOrderId] = useState(() => {
    return loadStoredEditorValue(realOrderStorageKeyPrefix, storeId) ?? ''
  })

  const { data, loading, fetchDataCancellable } = useFetch(configurationServices.getOrderTrackingPreviewOrder, {
    doInitialRequest: false,
    transformResponse: transformResponseOrderTrackingPreviewOrder,
  })

  const handleOrderLoadError = useCallback(
    error => {
      setSelectedRealOrderId('')

      if (isOrderNotFoundError(error)) {
        saveStoredEditorValue(realOrderStorageKeyPrefix, storeId, null)

        return
      }

      setToastInfo({ show: true, message: t('No se ha podido cargar el pedido. Inténtalo de nuevo.'), type: 'error' })
    },
    [storeId, setToastInfo, t]
  )

  useEffect(() => {
    if (!isRealOrderTab || !selectedRealOrderId) return

    fetchDataCancellable({ storeId, orderId: selectedRealOrderId }, undefined, handleOrderLoadError)
  }, [storeId, isRealOrderTab, selectedRealOrderId, fetchDataCancellable, handleOrderLoadError])

  const selectRealOrder = orderId => {
    saveStoredEditorValue(realOrderStorageKeyPrefix, storeId, orderId)
    setSelectedRealOrderId(orderId)
  }

  const realOrder = getDisplayedRealOrder({ fetchedOrder: data, selectedRealOrderId, loadingOrder: loading })

  const realOrderPreview = getRealOrderPreview({
    isRealOrderTab,
    realOrder,
    orderTrackingPreview,
    layout,
    getBlockVisibility,
    criteriaOptions,
    today: toStoreDateString(new Date().toISOString(), timeZone),
    timeZone,
  })

  return { selectedRealOrderId, selectRealOrder, realOrder, realOrderPreview }
}

export default useRealOrder
